import {
  AccordionGroup,
  BLOTTER_TABLE_FILTERS_CONTAINER_ID,
  Box,
  MixpanelEvent,
  Tab,
  TabList,
  TabPanels,
  TabSize,
  Tabs,
  tabLabelerEnumerated,
  useCallbackRef,
  useMixpanel,
  usePersistedTabs,
  usePortal,
  useTabs,
} from '@talos/kyoko';
import { useState } from 'react';
import { useBlotterTableTabCloning } from '../../../providers/AppConfigRedux/useBlotterTableTabCloning';
import { BalancesV2Blotter } from './BalancesV2Blotter';
import { defaultBalancesTabs } from './defaults';
import { BALANCES_V2_BLOTTER_PREFIX } from './tokens';
import type { BalancesV2TabProps } from './types';

const tabLabeler = tabLabelerEnumerated('Balances');
function getBlotterID(tabID: string | undefined) {
  return `${BALANCES_V2_BLOTTER_PREFIX}/${tabID}`;
}

export const BalancesV2 = () => {
  const mixpanel = useMixpanel();
  const [initialFiltersOpen, setInitialFiltersOpen] = useState(false);

  const handleSelect = useCallbackRef(() => setInitialFiltersOpen(false));
  const handleAdd = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.AddTab);
    setInitialFiltersOpen(true);
  });

  const persistedTabs = usePersistedTabs<BalancesV2TabProps>(BALANCES_V2_BLOTTER_PREFIX, {
    defaultInitialItems: defaultBalancesTabs,
    defaultInitialSelectedIndex: 0,
    onSelect: handleSelect,
  });

  const tabs = useTabs<BalancesV2TabProps>({
    ...persistedTabs,
    showAddTab: true,
    tabLabeler,
    onAdd: handleAdd,
    allowClosingLastTab: false,
    onClone: useBlotterTableTabCloning({ getBlotterID }),
  });

  const handleCloneTab = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.CloneTab);
    tabs.clone(tabs.selectedIndex);
  });

  const handleUpdateTab = useCallbackRef((updatedTab: BalancesV2TabProps) => {
    tabs.updateItem(updatedTab);
  });

  const { setPortalRef: filtersContainerRef } = usePortal(BLOTTER_TABLE_FILTERS_CONTAINER_ID);

  return (
    <Tabs {...tabs} h="100%" size={TabSize.Large} data-testid="balances-blotter-wrapper">
      <TabList isBordered rightItems={<Box ref={filtersContainerRef} />}>
        {tabs.items.map((tab, idx) => (
          <Tab key={idx} {...tab} />
        ))}
      </TabList>
      <TabPanels style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>
        {tabs.items.map(tab => {
          return (
            <AccordionGroup key={tab.id}>
              <BalancesV2Blotter
                key={tab.id}
                blotterID={getBlotterID(tab.id)}
                tab={tab}
                onUpdateTab={handleUpdateTab}
                onCloneTab={handleCloneTab}
                initialIsOpen={initialFiltersOpen}
              />
            </AccordionGroup>
          );
        })}
      </TabPanels>
    </Tabs>
  );
};
